import Box from '@mui/material/Box';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';

import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { RutterMappedRecord } from '~hooks/useRutter/models';
import { Nullable } from '~types/Nullable';

import { MAPPING_SELECT_WIDTH_PX } from './mappingColumnWidth';

type MappingOption = AutocompleteFieldOption<RutterMappedRecord>;

interface QuickBooksTypeaheadFormValues {
  item: Nullable<MappingOption>;
}

interface RenderTypeaheadProps {
  control: Control<QuickBooksTypeaheadFormValues>;
  isDisabled: boolean;
  name: 'item';
}

interface QuickBooksTypeaheadCellProps {
  dataTestId: string;
  onSave: (nextQuickBooksRecordId: Nullable<string>) => Promise<boolean>;
  renderTypeahead: (props: RenderTypeaheadProps) => ReactNode;
  value: Nullable<RutterMappedRecord>;
}

function toOption(record: Nullable<RutterMappedRecord>): Nullable<MappingOption> {
  return record ? { _entity: record, label: record.label, value: record.id } : null;
}

export default function QuickBooksTypeaheadCell({
  dataTestId,
  onSave,
  renderTypeahead,
  value,
}: QuickBooksTypeaheadCellProps) {
  const [isSaving, setIsSaving] = useState(false);
  // a save reloads the row before it resolves, so a rollback must use the reloaded value
  const latestValueRef = useRef(value);
  latestValueRef.current = value;
  const { control, setValue, watch } = useForm<QuickBooksTypeaheadFormValues>({
    defaultValues: { item: toOption(value) },
  });
  const selectedOption = useWatch({ control, name: 'item' });

  useEffect(() => {
    setValue('item', toOption(value));
  }, [value?.id, setValue]);

  const handleSelect = async (nextQuickBooksRecordId: Nullable<string>) => {
    setIsSaving(true);

    try {
      const saved = await onSave(nextQuickBooksRecordId);

      if (!saved) {
        setValue('item', toOption(latestValueRef.current));
      }
    } finally {
      setIsSaving(false);
    }
  };
  const handleSelectRef = useRef(handleSelect);
  handleSelectRef.current = handleSelect;

  // Only a user's pick has type "change"; resetting the value with setValue does not.
  useEffect(() => {
    const subscription = watch((formValues, { type }) => {
      if (type === 'change') {
        void handleSelectRef.current(formValues.item?.value ?? null);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Box
      data-test-id={dataTestId}
      // a long mapped name is clipped by the input, so hovering shows it in full
      title={selectedOption?.label || undefined}
      sx={{ display: 'flex', width: MAPPING_SELECT_WIDTH_PX }}
    >
      {renderTypeahead({ control, isDisabled: isSaving, name: 'item' })}
    </Box>
  );
}
